import { ActionTree } from 'vuex';
import { GroupState } from './types';
import { RootState } from '../types';
import FirebaseGateway from '@/classes/firebaseGateway';
import { Quest, Journal, QuestTask, QuestTaskDescription } from '@/interfaces/journal/journal';
import { Round } from '@/interfaces/rounds/rounds';
import { Handout } from '@/interfaces/handouts/handouts';
import FirebaseRealtime from '@/classes/firebaseRealtime';

export const actions: ActionTree<GroupState, RootState> = {
  loadJournal({ rootState, commit }) {
    const currentGroup = rootState.currentGroup;
    if (!currentGroup) {
      throw new Error('No current group set');
    }

    const gateway = new FirebaseGateway();
    const journalPromise = gateway.getJournal(currentGroup);
    journalPromise.then((journal: Journal) => {
      commit('setJournal', journal);
    });
    // Also start watching the journal for changes
    FirebaseRealtime.watchJournal(commit, currentGroup);
    return journalPromise;
  },

  /**
   * Adds a new quest to the journal
   */
  addNewQuest({ commit, rootState }, quest: Quest) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const gateway = new FirebaseGateway();
    gateway.addQuest(rootState.currentGroup, quest).then(() => {
      commit('addQuest', quest);
    });
  },

  deleteQuest({ dispatch, rootState }, questId) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const gateway = new FirebaseGateway();
    return gateway.deleteQuest(rootState.currentGroup, questId).then(() => {
      return dispatch('loadJournal');
    });
  },

  updateQuest({ dispatch, rootState }, quest) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const gateway = new FirebaseGateway();
    return gateway.updateQuest(rootState.currentGroup, quest).then(() => {
      return dispatch('loadJournal');
    });
  },

  changeQuestOrder({ dispatch, rootState }, payload) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const gateway = new FirebaseGateway();
    return gateway.changeQuestOrder(rootState.currentGroup, payload.quest, payload.newIndex).then(() => {
      return dispatch('loadJournal');
    });
  },

  /**
   * Adds a new quest to the journal
   */
  addNewTask({ commit, rootState }, payload) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const user = rootState.user;
    if (user === null) {
      throw new Error('user is null');
    }
    const questTask: QuestTask = {
      title: payload.title,
      id: Math.round(Math.random() * 100000000).toString(),
      descriptions: [],
      author: user.uid,
    };
    const questId = payload.questId;
    const gateway = new FirebaseGateway();
    const returnPromise = gateway.addQuestTask(rootState.currentGroup, questId, questTask);

    returnPromise.then(() => {
      commit('addQuestTask', { questTask, questId });
    });
    return returnPromise;
  },

  /**
   * Adds a new quest to the journal
   */
  deleteQuestTask({ dispatch, rootState }, payload) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const user = rootState.user;
    if (user === null) {
      throw new Error('user is null');
    }
    const questId = payload.questId;
    const questTaskId = payload.questTaskId;
    const gateway = new FirebaseGateway();
    const returnPromise = gateway.deleteQuestTask(rootState.currentGroup, questId, questTaskId);

    return returnPromise.then(() => {
      return dispatch('loadJournal');
    });
  },

  /**
   * Adds a new quest to the journal
   */
  addNewTaskDescription({ commit, rootState }, payload) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('No current group set');
    }
    const user = rootState.user;
    if (user === null) {
      throw new Error('user is null');
    }
    const questTaskDescription: QuestTaskDescription = {
      text: payload.text,
      id: Math.round(Math.random() * 100000000).toString(),
      author: user.uid,
      createdAt: Math.round(new Date().getTime() / 1000),
    };
    const questId = payload.questId;
    const questTaskId = payload.questTaskId;
    const gateway = new FirebaseGateway();
    const returnPromise = gateway.addQuestTaskDescription(
      rootState.currentGroup,
      questId,
      questTaskId,
      questTaskDescription,
    );

    returnPromise.then(() => {
      commit('addQuestTaskDescription', { questTaskDescription, questId, questTaskId });
    });
    return returnPromise;
  },

  saveRound({ commit, rootState }, round: Round) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('Currentgroup not set');
    }
    return FirebaseGateway.saveRound(rootState.currentGroup, round)
      .then((roundId) => {
        round.id = roundId;
        commit('setRound', round);
        return round;
      })
      .catch((error) => {
        console.log('error in actions caught', error);
        throw error;
      });
  },
  loadRound({ commit, rootState }, roundId) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('Currentgroup not set');
    }
    return FirebaseGateway.loadRound(rootState.currentGroup, roundId).then((round) => {
      commit('setRound', round);
      return round;
    });
  },
  loadRoundShorts({ commit, rootState }) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('Currentgroup not set');
    }
    return FirebaseGateway.loadGroup(rootState.currentGroup).then((groupData: GroupState) => {
      if (Array.isArray(groupData.roundsShort) && groupData.roundsShort.length > 0) {
        commit('setRoundShorts', groupData.roundsShort);
      }
      commit('setGroupMembers', groupData.members);
      return groupData.roundsShort;
    });
  },
  loadHandouts({ commit, rootState }) {
    if (!rootState.currentGroup || !rootState.user) {
      throw new Error('Either group or user is null');
    }
    return FirebaseGateway.loadHandouts(rootState.currentGroup, rootState.user.uid).then((handouts) => {
      commit('setHandouts', handouts);
      return handouts;
    });
  },
  uploadHandout({ commit, rootState }, handout: Handout) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('Currentgroup not set');
    }

    return FirebaseGateway.uploadHandout(rootState.currentGroup, handout).then((handoutId) => {
      handout.id = handoutId;
      commit('setHandout', handout);
      return true;
    });
  },
  deleteHandout({ commit, rootState }, handout: Handout) {
    if (typeof rootState.currentGroup === 'undefined') {
      throw new Error('Currentgroup not set');
    }

    return FirebaseGateway.deleteHandout(rootState.currentGroup, handout).then((handoutId) => {
      commit('removeHandout', handout);
      return true;
    });
  },

  loadSession({ commit, rootState }) {
    const currentGroup = rootState.currentGroup;
    if (!currentGroup) {
      throw new Error('No current group set');
    }

    // start watching the session for changes
    return FirebaseRealtime.watchSession(commit, currentGroup);
  },

  startSession({ rootState }) {
    if (rootState.user === null) {
      throw new Error('User is null');
    }
    if (rootState.currentGroup === null || typeof rootState.currentGroup === 'undefined') {
      throw new Error('currentGroup is null');
    }
    const currentUser = rootState.user.uid;
    const currentGroup = rootState.currentGroup;
    FirebaseRealtime.startSession(currentGroup, currentUser);
  },

  stopSession({ rootState }) {
    if (rootState.currentGroup === null || typeof rootState.currentGroup === 'undefined') {
      throw new Error('currentGroup is null');
    }
    const currentGroup = rootState.currentGroup;
    FirebaseRealtime.stopSession(currentGroup);
  },

  setSessionAudio({ rootState }, audioState: boolean) {
    if (rootState.currentGroup === null || typeof rootState.currentGroup === 'undefined') {
      throw new Error('currentGroup is null');
    }
    const currentGroup = rootState.currentGroup;
    FirebaseRealtime.setSessionAudio(currentGroup, audioState);
  },
};
